import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import Icon from '../../../components/dashboard/DashboardIcons';
import { toast } from '../../../lib/toast';
import { API, auth, Field, Modal, PrimaryBtn, errMsg, inputCls, fmtDate, DAY_TYPE_LABELS, PLAN_STATUS_LABELS, PLAN_STATUS_COLORS, StatusPill } from './shared';

const COACH_TRANSITIONS = {
  draft: ['pending_approval', 'active', 'archived'], approved: ['active', 'archived'], changes_requested: ['draft', 'pending_approval'],
  active: ['completed', 'archived'], completed: ['archived'], pending_approval: ['archived'], archived: [],
};

export default function PlanDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddBlock, setShowAddBlock] = useState(false);
  const [blockForm, setBlockForm] = useState({ date: '', day_type: 'training', exercises: [] });
  const [exerciseOptions, setExerciseOptions] = useState([]);
  const [showShare, setShowShare] = useState(false);
  const [memberships, setMemberships] = useState([]);
  const [shareForm, setShareForm] = useState({ share_scope: 'organization', organization_id: '', permission: 'view' });
  const [reviews, setReviews] = useState([]);

  const load = async () => {
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${API}/coach-studio/plans/${id}`, auth());
      setPlan(res.data);
    } catch (e) { setError(errMsg(e, 'تعذر تحميل الخطة')); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    axios.get(`${API}/coach-studio/exercises`, { ...auth(), params: { scope: 'mine', page_size: 50 } }).then((r) => setExerciseOptions(r.data.items)).catch(() => {});
  }, []);

  const openShare = async () => {
    try {
      const res = await axios.get(`${API}/coaches/me/memberships`, auth());
      setMemberships((res.data || []).filter((m) => m.status === 'active'));
      setShowShare(true);
    } catch (e) { toast.error(errMsg(e, 'تعذر تحميل المنشآت')); }
  };

  const submitShare = async () => {
    try {
      await axios.post(`${API}/coach-studio/shares`, { content_type: 'plan', content_id: id, ...shareForm }, auth());
      toast.success('تمت مشاركة الخطة');
      setShowShare(false); load();
    } catch (e) { toast.error(errMsg(e, 'تعذر المشاركة')); }
  };

  const requestApproval = async () => {
    if (!shareForm.organization_id && !plan.organization_id) { toast.error('حدد المنشأة أولاً عبر المشاركة'); return; }
    try {
      await axios.post(`${API}/coach-studio/plans/${id}/request-approval?organization_id=${plan.organization_id || shareForm.organization_id}`, {}, auth());
      toast.success('تم إرسال طلب الاعتماد');
      load();
    } catch (e) { toast.error(errMsg(e, 'تعذر إرسال الطلب')); }
  };

  const changeStatus = async (status) => {
    try { await axios.put(`${API}/coach-studio/plans/${id}/status?status=${status}`, {}, auth()); toast.success('تم تحديث حالة الخطة'); load(); }
    catch (e) { toast.error(errMsg(e, 'تعذر تحديث الحالة')); }
  };

  const loadReviews = async () => {
    try { const res = await axios.get(`${API}/coach-studio/plans/${id}/reviews`, auth()); setReviews(res.data); }
    catch (e) { /* silent */ }
  };

  const addBlock = async () => {
    if (!blockForm.date) { toast.error('التاريخ مطلوب'); return; }
    try {
      await axios.post(`${API}/coach-studio/plans/${id}/blocks`, blockForm, auth());
      toast.success('تمت إضافة اليوم التدريبي');
      setShowAddBlock(false); setBlockForm({ date: '', day_type: 'training', exercises: [] });
      load();
    } catch (e) { toast.error(errMsg(e, 'تعذر الإضافة')); }
  };

  const deleteBlock = async (blockId) => {
    if (!window.confirm('حذف هذا اليوم التدريبي؟')) return;
    try { await axios.delete(`${API}/coach-studio/blocks/${blockId}`, auth()); load(); } catch (e) { toast.error(errMsg(e, 'تعذر الحذف')); }
  };

  const duplicateBlock = async (blockId) => {
    try { await axios.post(`${API}/coach-studio/blocks/${blockId}/duplicate`, {}, auth()); toast.success('تم نسخ اليوم'); load(); }
    catch (e) { toast.error(errMsg(e, 'تعذر النسخ')); }
  };

  const addExerciseToBlock = (exId) => {
    setBlockForm((f) => ({ ...f, exercises: [...f.exercises, { exercise_id: exId, order: f.exercises.length, duration_minutes: 15 }] }));
  };

  if (loading) return <div className="p-8 text-center text-sm text-slate-400" data-testid="plan-detail-loading">جاري التحميل...</div>;
  if (error) return <div className="p-8 text-center text-sm text-red-500" data-testid="plan-detail-error">{error}</div>;
  if (!plan) return null;

  return (
    <div className="space-y-5" data-testid="plan-detail-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-slate-900">{plan.name}</h2>
          <div className="mt-1 flex items-center gap-2 text-xs text-slate-500">
            <StatusPill status={plan.status} labels={PLAN_STATUS_LABELS} colors={PLAN_STATUS_COLORS} />
            <span>{fmtDate(plan.start_date)} — {fmtDate(plan.end_date)}</span>
            <span>إجمالي المدة: {plan.total_duration_minutes} دقيقة</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {plan.is_owner && (COACH_TRANSITIONS[plan.status] || []).map((s) => (
            <button key={s} onClick={() => changeStatus(s)} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs" data-testid={`plan-status-${s}-btn`}>{PLAN_STATUS_LABELS[s]}</button>
          ))}
          {plan.is_owner && <button onClick={openShare} className="rounded-lg border border-teal-200 px-3 py-1.5 text-xs text-teal-700" data-testid="plan-share-btn">مشاركة</button>}
          {plan.is_owner && plan.status === 'draft' && <button onClick={requestApproval} className="rounded-lg border border-amber-200 px-3 py-1.5 text-xs text-amber-700" data-testid="plan-request-approval-btn">طلب اعتماد</button>}
          <button onClick={loadReviews} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs" data-testid="plan-view-reviews-btn">سجل المراجعات</button>
          <button onClick={() => window.print()} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs" data-testid="plan-print-btn">طباعة</button>
        </div>
      </div>

      {reviews.length > 0 && (
        <div className="rounded-2xl border border-slate-200/70 bg-white p-3 shadow-[0_1px_3px_rgba(0,0,0,0.04)] text-xs" data-testid="plan-reviews-list">
          {reviews.map((r) => <div key={r.id} className="border-b border-slate-100 py-1">{r.review_type} — {r.note || '—'} — {fmtDate(r.created_at)}</div>)}
        </div>
      )}

      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-slate-900">الأيام التدريبية</h3>
        {plan.is_owner && <button onClick={() => setShowAddBlock(true)} className="flex items-center gap-1 rounded-lg bg-[#16233b] px-3 py-1.5 text-xs text-white" data-testid="plan-add-block-btn"><Icon name="plus" className="w-3 h-3" /> إضافة يوم</button>}
      </div>

      <div className="space-y-3" data-testid="plan-blocks-list">
        {plan.blocks.length === 0 ? <p className="text-sm text-slate-400">لا توجد أيام مضافة بعد</p> : plan.blocks.map((b) => (
          <div key={b.id} className="rounded-2xl border border-slate-200/70 bg-white p-3 shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid={`plan-block-${b.id}`}>
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-800">{fmtDate(b.date)} — {DAY_TYPE_LABELS[b.day_type]}</span>
              {plan.is_owner && (
                <div className="flex gap-2 text-xs">
                  <button onClick={() => duplicateBlock(b.id)} className="text-slate-500" data-testid={`plan-block-duplicate-${b.id}`}>نسخ</button>
                  <button onClick={() => deleteBlock(b.id)} className="text-red-500" data-testid={`plan-block-delete-${b.id}`}>حذف</button>
                </div>
              )}
            </div>
            <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-600">
              {b.exercises.map((e, i) => <span key={i} className="rounded-full bg-slate-100 px-2 py-0.5">{exerciseOptions.find((x) => x.id === e.exercise_id)?.title || e.exercise_id} ({e.duration_minutes}د)</span>)}
            </div>
            <div className="mt-1 text-xs text-slate-400">إجمالي: {b.total_duration_minutes} دقيقة</div>
          </div>
        ))}
      </div>

      <Modal open={showAddBlock} title="إضافة يوم تدريبي" onClose={() => setShowAddBlock(false)} wide>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="التاريخ" required><input type="date" value={blockForm.date} onChange={(e) => setBlockForm({ ...blockForm, date: e.target.value })} className={inputCls} data-testid="block-date-input" /></Field>
            <Field label="نوع اليوم">
              <select value={blockForm.day_type} onChange={(e) => setBlockForm({ ...blockForm, day_type: e.target.value })} className={inputCls} data-testid="block-day-type-select">
                {Object.entries(DAY_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
          </div>
          <Field label="التمارين">
            <div className="max-h-40 overflow-y-auto rounded-lg border border-slate-200 p-2">
              {exerciseOptions.map((ex) => (
                <button key={ex.id} type="button" onClick={() => addExerciseToBlock(ex.id)} className="block w-full rounded p-1.5 text-right text-xs hover:bg-slate-50" data-testid={`block-add-exercise-${ex.id}`}>{ex.title}</button>
              ))}
            </div>
            <div className="mt-2 flex flex-wrap gap-1">
              {blockForm.exercises.map((e, i) => (
                <span key={i} className="rounded-full bg-teal-50 px-2 py-1 text-xs text-teal-700">
                  {exerciseOptions.find((x) => x.id === e.exercise_id)?.title}
                  <input type="number" value={e.duration_minutes} onChange={(ev) => { const arr = [...blockForm.exercises]; arr[i] = { ...e, duration_minutes: Number(ev.target.value) }; setBlockForm({ ...blockForm, exercises: arr }); }} className="mx-1 w-12 rounded border border-teal-200 px-1" />
                  <button onClick={() => setBlockForm({ ...blockForm, exercises: blockForm.exercises.filter((_, idx) => idx !== i) })}>×</button>
                </span>
              ))}
            </div>
          </Field>
          <PrimaryBtn onClick={addBlock} data-testid="block-submit-btn">إضافة اليوم</PrimaryBtn>
        </div>
      </Modal>

      <Modal open={showShare} title="مشاركة الخطة" onClose={() => setShowShare(false)}>
        <div className="space-y-3">
          <Field label="مستوى المشاركة">
            <select value={shareForm.share_scope} onChange={(e) => setShareForm({ ...shareForm, share_scope: e.target.value })} className={inputCls} data-testid="share-scope-select">
              <option value="organization">مع المنشأة</option>
              <option value="staff">مع أعضاء طاقم محددين</option>
            </select>
          </Field>
          {shareForm.share_scope === 'organization' && (
            <Field label="المنشأة">
              <select value={shareForm.organization_id} onChange={(e) => setShareForm({ ...shareForm, organization_id: e.target.value })} className={inputCls} data-testid="share-org-select">
                <option value="">اختر المنشأة</option>
                {memberships.map((m) => <option key={m.id} value={m.club_id}>{m.club_id}</option>)}
              </select>
            </Field>
          )}
          <Field label="صلاحية المستلم">
            <select value={shareForm.permission} onChange={(e) => setShareForm({ ...shareForm, permission: e.target.value })} className={inputCls} data-testid="share-permission-select">
              <option value="view">مشاهدة فقط</option>
              <option value="copy">نسخ</option>
            </select>
          </Field>
          <PrimaryBtn onClick={submitShare} data-testid="share-submit-btn">مشاركة</PrimaryBtn>
        </div>
      </Modal>
    </div>
  );
}
